import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { openPage } from '@cedalo/webui/src/actions/actions';
import { Overlay } from '@cedalo/webui/src/components/HelperComponent/Overlay';
import gatewayClient from '@cedalo/webui/src/helper/GatewayClient';
import { intl } from '@cedalo/webui/src/helper/IntlGlobalProvider';
import { AdminPageLayout } from '@cedalo/webui/src/layouts/AdminPageLayout';
import { Path } from '@cedalo/webui/src/helper/Path';

const workspaceShape = PropTypes.shape({
	name: PropTypes.string
});

const CreateWorkspaceForm = (props) => {
	const { workspace, errors, valid, disabled, pristine, onNameUpdate, onCancel, onSubmit } = props;

	const errorsMappings = {
		NAME_INVALID: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInvalid' }, {}),
		NAME_IN_USE: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInUse' }, {}),
		NAME_REQUIRED: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameRequired' }, {}),
		UNEXPECTED_ERROR: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorUnexpected' }, {})
	};

	const getError = (code) => (code ? errorsMappings[code] || errorsMappings.UNEXPECTED_ERROR : undefined);

	return (
		<form>
			<Grid container spacing={2}>
				<Grid item container spacing={8} justify="space-between" alignItems="center">
					<Grid item>
						<Typography variant="h5">
							<FormattedMessage id="Extensions.Admin.Workspace.add" defaultMessage="Add New Workspace" />
						</Typography>
					</Grid>
					{errors.form ? (
						<Grid item>
							<Typography color="error" variant="subtitle1">
								{getError(errors.form)}
							</Typography>
						</Grid>
					) : undefined}
				</Grid>
				<Grid item xs={12} sm={12}>
					<TextField
						required
						variant="outlined"
						size="small"
						id="name"
						label={<FormattedMessage id="Extensions.Admin.Workspace.labelName" defaultMessage="Name" />}
						fullWidth
						error={!!errors.name}
						helperText={getError(errors.name)}
						disabled={disabled}
						value={workspace.name}
						onChange={(event) => onNameUpdate(event.target.value)}
					/>
				</Grid>
				<Grid container item spacing={2} xs={12} justify="flex-end" direction="row">
					<Grid item>
						<Button variant="outlined" disabled={disabled} onClick={onCancel}>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonCancel" defaultMessage="Cancel" />
						</Button>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							color="primary"
							onClick={onSubmit}
							disabled={pristine || !valid || disabled}
						>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonAdd" defaultMessage="Add" />
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
};

CreateWorkspaceForm.propTypes = {
	workspace: workspaceShape.isRequired,
	errors: PropTypes.shape({
		name: PropTypes.string
	}).isRequired,
	valid: PropTypes.bool.isRequired,
	disabled: PropTypes.bool.isRequired,
	pristine: PropTypes.bool.isRequired,
	onNameUpdate: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired
};

const createWorkspaceReducer = (state, action) => {
	switch (action.type) {
		case 'set_name':
			return {
				...state,
				pristine: false,
				workspace: { ...state.workspace, name: action.data },
				errors: {
					...state.errors,
					name: !action.data ? 'NAME_REQUIRED' : undefined
				}
			};
		case 'save':
			return {
				...state,
				savePending: true
			};
		case 'saving_error':
			return {
				...state,
				savePending: false,
				pristine: true,
				errors: action.data
			};
		case 'saving_success':
			return {
				...state,
				savePending: false,
				saved: true
			};
		default:
			throw new Error('Unkown action');
	}
};

const CREATE_WORKSPACE_MUTATION = `
mutation CreateWorkspace($workspace: WorkspaceInput!) {
	createWorkspace(workspace: $workspace) {
		workspace {
			id
			name
		}
		success
		code
		fieldErrors {
			name
		}
	}
}
`;

const hasFieldError = (errors) =>
	Object.entries(errors).filter(([key, value]) => key !== 'form' && !!value).length > 0;

export const CreateWorkspacePageComponent = (props) => {
	const { onCancel, onSubmit } = props;
	const [state, dispatch] = useReducer(createWorkspaceReducer, {
		pristine: true,
		workspace: {
			name: ''
		},
		errors: {},
		savePending: false,
		saved: false
	});

	useEffect(() => {
		if (state.saved) {
			setTimeout(() => {
				onSubmit(state.workspace);
			}, 500);
		}
	}, [state.saved]);

	const hasError = hasFieldError(state.errors);
	const hasRequired = state.workspace.name;
	const isValid = !!(!hasError && hasRequired);

	const saveWorkspace = async () => {
		dispatch({ type: 'save' });
		try {
			const {
				createWorkspace: { success, fieldErrors, code, workspace }
			} = await gatewayClient.graphql(CREATE_WORKSPACE_MUTATION, {
				workspace: state.workspace
			});
			if (success) {
				dispatch({ type: 'saving_success', data: workspace });
			} else if (fieldErrors) {
				dispatch({ type: 'saving_error', data: fieldErrors });
			} else {
				dispatch({ type: 'saving_error', data: { form: code } });
			}
		} catch (error) {
			dispatch({ type: 'saving_error', data: { form: 'UNEXPECTED_ERROR' } });
			console.error(error);
		}
	};

	return (
		<AdminPageLayout page="workspaces" documentTitle={intl.formatMessage({ id: 'Administration' })}>
			<div
				style={{
					minHeight: '100%',
					padding: '24px'
				}}
			>
				<Paper style={{ padding: '32px', maxWidth: '960px', margin: 'auto', position: 'relative' }}>
					<CreateWorkspaceForm
						workspace={state.workspace}
						errors={state.errors}
						pristine={state.pristine}
						valid={isValid}
						disabled={state.savePending || state.saved}
						onCancel={onCancel}
						onSubmit={saveWorkspace}
						onNameUpdate={(value) => dispatch({ type: 'set_name', data: value })}
					/>
					{state.savePending && (
						<Overlay>
							<CircularProgress style={{ width: '24px', height: '24px' }} />
						</Overlay>
					)}
					{state.saved && (
						<Overlay>
							<CheckIcon color="primary" />
						</Overlay>
					)}
				</Paper>
			</div>
		</AdminPageLayout>
	);
};

CreateWorkspacePageComponent.propTypes = {
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired
};

const mapDispatchToProps = {
	onCancel: () => openPage(Path.workspaces()),
	onSubmit: () => openPage(Path.workspaces())
};

export const CreateWorkspacePage = connect(null, mapDispatchToProps)(CreateWorkspacePageComponent);



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/workspace/CreateWorkspacePage.js